import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { isBedbankOffer, isBundleOffer, isLEHotel } from 'lib/offer/offerTypes'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import {
  getBedbankAndFlightsChildMaxAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
  getBundleAndSaveMaxTeenagerAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getOfferMaxTeenagerAge,
} from 'lib/offer/offerUtils'
import type { OfferPageAssistantSource } from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotTypes'

export type AssistantOccupancyAgeThresholds = Partial<
  Pick<App.AgeCategoryThresholds, 'maxChildAge' | 'maxInfantAge' | 'maxTeenagerAge'>
>

export interface SearchStringAgeThresholds {
  maxChildAge?: number
  maxInfantAge?: number
  maxTeenagerAge?: number
}

export function isOccupancyThresholdOffer(
  offer: App.AnyOffer | undefined,
): offer is App.Offer | App.BedbankOffer | App.BundleOffer {
  if (!offer) {
    return false
  }

  if (isBundleOffer(offer)) {
    return 'highlights' in offer
  }

  if (isBedbankOffer(offer)) {
    return 'description' in offer
  }

  if (isLEHotel(offer)) {
    return 'highlights' in offer
  }

  return false
}

export function hasMeaningfulAgeCategoryThresholds(
  thresholds?: AssistantOccupancyAgeThresholds | null,
): thresholds is AssistantOccupancyAgeThresholds {
  if (!thresholds) {
    return false
  }

  return (
    thresholds.maxChildAge !== undefined ||
    (thresholds.maxInfantAge !== undefined && thresholds.maxInfantAge >= 0) ||
    (thresholds.maxTeenagerAge !== undefined && thresholds.maxTeenagerAge > 0)
  )
}

function thresholdsFromHotelOffer(offer: App.HotelOffer): AssistantOccupancyAgeThresholds {
  return {
    maxChildAge: getOfferAndFlightsMaxChildAge(offer),
    maxInfantAge: getOfferMaxInfantAge(offer),
    maxTeenagerAge: getOfferMaxTeenagerAge(offer),
  }
}

function thresholdsFromBedbankOffer(offer: App.BedbankOffer): AssistantOccupancyAgeThresholds {
  return {
    maxChildAge: getBedbankAndFlightsChildMaxAge(offer),
    maxInfantAge: -1,
    maxTeenagerAge: -1,
  }
}

function thresholdsFromPropertyRoom(room?: App.Property.Room): AssistantOccupancyAgeThresholds | undefined {
  const thresholds = room?.ageCategoryThresholds
  return hasMeaningfulAgeCategoryThresholds(thresholds) ? thresholds : undefined
}

function thresholdsFromProperty(
  property: App.Property.Property,
  propertyRoomId?: string,
): AssistantOccupancyAgeThresholds | undefined {
  if (propertyRoomId) {
    return thresholdsFromPropertyRoom(property.rooms[propertyRoomId])
  }

  const fallbackRoom = Object.values(property.rooms).find(room =>
    hasMeaningfulAgeCategoryThresholds(room.ageCategoryThresholds),
  )
  return fallbackRoom ? thresholdsFromPropertyRoom(fallbackRoom) : undefined
}

export function resolveAssistantOccupancyAgeThresholds(input: {
  ageThresholds?: AssistantOccupancyAgeThresholds
  offer?: App.Offer | App.BedbankOffer | App.BundleOffer
  offerPageState?: OfferPageState
}): AssistantOccupancyAgeThresholds | undefined {
  if (input.ageThresholds) {
    return input.ageThresholds
  }

  if (input.offerPageState) {
    return getAssistantOccupancyAgeThresholdsFromOfferPageState(input.offerPageState)
  }

  if (input.offer) {
    return getAssistantOccupancyAgeThresholdsFromOffer(input.offer)
  }

  return undefined
}

export function toSearchStringAgeThresholds(
  thresholds?: AssistantOccupancyAgeThresholds,
): SearchStringAgeThresholds {
  if (!thresholds) {
    return {}
  }

  return {
    maxChildAge: thresholds.maxChildAge,
    maxInfantAge: thresholds.maxInfantAge !== undefined && thresholds.maxInfantAge >= 0
      ? thresholds.maxInfantAge
      : undefined,
    maxTeenagerAge: thresholds.maxTeenagerAge !== undefined && thresholds.maxTeenagerAge > 0
      ? thresholds.maxTeenagerAge
      : undefined,
  }
}

export function getAssistantOccupancyAgeThresholdsFromOffer(
  offer?: App.Offer | App.BedbankOffer | App.BundleOffer,
): AssistantOccupancyAgeThresholds | undefined {
  if (!offer) {
    return undefined
  }

  if (isBundleOffer(offer)) {
    return {
      maxChildAge: getBundleAndSaveMaxChildAge(offer),
      maxInfantAge: getBundleAndSaveMaxInfantAge(offer),
      maxTeenagerAge: getBundleAndSaveMaxTeenagerAge(offer),
    }
  }

  if (isLEHotel(offer)) {
    return thresholdsFromHotelOffer(offer)
  }

  if (isBedbankOffer(offer)) {
    return thresholdsFromBedbankOffer(offer)
  }

  return undefined
}

export function getAssistantOccupancyAgeThresholdsFromOfferPageState(
  offerPageState: OfferPageState,
): AssistantOccupancyAgeThresholds | undefined {
  // Only PropertyContainer.tsx ever sets `.property` on OfferPageState (Flash/Bedbank containers
  // never do), so its presence alone -- regardless of whether the paired offer is LPC-backed
  // ("lme_property") or bedbank-backed ("spp_property") -- identifies a property page. Property
  // room thresholds are the source of truth for both; checked before the offer-based fallthrough
  // below so an offer never overrides/fills in for incomplete property data. Previously this was
  // gated on `property && bedbankOffer`, which meant LPC-backed property pages fell through to
  // offer-based thresholds below (getAssistantOccupancyAgeThresholdsFromOffer never returns
  // undefined for a real hotel offer, so the property-only fallback that used to sit after it
  // was unreachable dead code for that case).
  if (offerPageState.property) {
    return thresholdsFromProperty(offerPageState.property, offerPageState.propertyRoomId)
  }

  return getAssistantOccupancyAgeThresholdsFromOffer(
    offerPageState.offer ?? offerPageState.bedbankOffer ?? offerPageState.bundleOffer,
  )
}

export function getAssistantOccupancyAgeThresholdsFromSnapshotSource(
  source: OfferPageAssistantSource,
): AssistantOccupancyAgeThresholds | undefined {
  switch (source.variant) {
    case 'flash':
      return thresholdsFromHotelOffer(source.offer)
    case 'bedbank':
      return thresholdsFromBedbankOffer(source.offer)
    case 'lme_property':
    case 'spp_property':
      return thresholdsFromProperty(source.property, source.selection.propertyRoomId)
    default:
      return assertUnreachable(source)
  }
}
